import React, { useState } from "react";
import {
  Check,
  X,
  RefreshCw,
  Home,
  ShoppingBag,
  Bike,
  Car,
  Train,
  Utensils,
} from "lucide-react";
import { motion } from "framer-motion";
import ModuleNavigation from "../components/ModuleNavigation";

// Quiz questions data
const quizQuestions = [
  {
    id: 1,
    question:
      "Which of the following contributes the most to an average person's carbon footprint in developed countries?",
    options: [
      "Food consumption",
      "Transportation",
      "Home energy use",
      "Online streaming",
    ],
    correctAnswer: 1,
    explanation:
      "Transportation, especially personal car use and air travel, typically represents the largest portion of an individual's carbon footprint in most developed countries.",
  },
  {
    id: 2,
    question:
      "By what percentage could global carbon emissions be reduced if everyone switched to a plant-based diet?",
    options: ["5-10%", "15-20%", "25-30%", "40-50%"],
    correctAnswer: 1,
    explanation:
      "Studies suggest that a global shift to plant-based diets could reduce food-related emissions by 70%, which translates to about 15-20% of total global carbon emissions.",
  },
  {
    id: 3,
    question: "What is a carbon sink?",
    options: [
      "A device that removes carbon from the air",
      "A natural feature that absorbs more carbon than it releases",
      "A type of carbon tax",
      "A measurement of carbon emissions",
    ],
    correctAnswer: 1,
    explanation:
      "A carbon sink is a natural reservoir (like forests, oceans, and soil) that absorbs and stores carbon dioxide from the atmosphere, helping to reduce atmospheric CO₂ concentrations.",
  },
  {
    id: 4,
    question:
      "Which action has the highest carbon reduction potential for an individual?",
    options: [
      "Using reusable shopping bags",
      "Having one fewer child",
      "Recycling regularly",
      "Buying local produce",
    ],
    correctAnswer: 1,
    explanation:
      "While all these actions help, research shows that having one fewer child has the highest carbon reduction impact per person (about 58 tonnes CO₂e per year), as it affects emissions across generations.",
  },
  {
    id: 5,
    question:
      "Approximately how much has the atmospheric concentration of CO₂ increased since pre-industrial times?",
    options: ["About 10%", "About 25%", "About 50%", "About 70%"],
    correctAnswer: 2,
    explanation:
      "Atmospheric CO₂ has increased from about 280 ppm in pre-industrial times to over 420 ppm today, which is an increase of approximately 50%.",
  },
];

// Simulator activity options
const simulatorOptions = {
  commute: [
    { id: "car", label: "Drive alone", icon: Car, emissions: 12.0 },
    { id: "carpool", label: "Carpool", icon: Car, emissions: 6.0 },
    { id: "bus", label: "Take the bus", icon: Train, emissions: 4.5 },
    { id: "bike", label: "Ride a bike", icon: Bike, emissions: 0 },
    { id: "walk", label: "Walk", icon: ShoppingBag, emissions: 0 },
  ],
  lunch: [
    { id: "beef", label: "Beef burger", icon: Utensils, emissions: 7.0 },
    {
      id: "chicken",
      label: "Chicken sandwich",
      icon: Utensils,
      emissions: 2.5,
    },
    {
      id: "vegetarian",
      label: "Vegetarian meal",
      icon: Utensils,
      emissions: 1.0,
    },
    { id: "vegan", label: "Vegan meal", icon: Utensils, emissions: 0.5 },
    { id: "home", label: "Packed lunch", icon: Home, emissions: 0.8 },
  ],
  evening: [
    { id: "movie", label: "Drive to movie theater", icon: Car, emissions: 4.0 },
    {
      id: "restaurant",
      label: "Dine at restaurant",
      icon: Utensils,
      emissions: 5.0,
    },
    {
      id: "streaming",
      label: "Stream movie at home",
      icon: Home,
      emissions: 0.2,
    },
    {
      id: "mall",
      label: "Shopping at mall",
      icon: ShoppingBag,
      emissions: 6.0,
    },
    { id: "park", label: "Visit local park", icon: Home, emissions: 0.5 },
  ],
};

const Quiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const [activeTab, setActiveTab] = useState("quiz");

  // Simulator state
  const [simulatorChoices, setSimulatorChoices] = useState({
    commute: null,
    lunch: null,
    evening: null,
  });

  // Handle quiz answer selection
  const handleAnswerSelect = (answerIndex) => {
    if (showResult) return;

    setSelectedAnswer(answerIndex);
    setShowResult(true);

    if (answerIndex === quizQuestions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
  };

  // Move to next question
  const handleNextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setQuizCompleted(true);
    }
  };

  // Reset quiz
  const handleResetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setQuizCompleted(false);
  };

  // Handle simulator choice selection
  const handleSimulatorChoice = (category, option) => {
    setSimulatorChoices({
      ...simulatorChoices,
      [category]: option,
    });
  };

  // Calculate total emissions from simulator choices
  const calculateTotalEmissions = () => {
    let total = 0;

    if (simulatorChoices.commute) {
      total += simulatorChoices.commute.emissions;
    }

    if (simulatorChoices.lunch) {
      total += simulatorChoices.lunch.emissions;
    }

    if (simulatorChoices.evening) {
      total += simulatorChoices.evening.emissions;
    }

    return total.toFixed(1);
  };

  // Reset simulator
  const handleResetSimulator = () => {
    setSimulatorChoices({
      commute: null,
      lunch: null,
      evening: null,
    });
  };

  // Get feedback text based on emissions
  const getSimulatorFeedback = () => {
    const total = Number(calculateTotalEmissions());

    if (total < 5) {
      return {
        text: "Excellent! Your choices result in very low carbon emissions.",
        color: "text-status-success",
      };
    } else if (total < 10) {
      return {
        text: "Good job! Your carbon footprint is moderate.",
        color: "text-status-attention",
      };
    } else {
      return {
        text: "Your choices result in significant carbon emissions. Consider more sustainable options.",
        color: "text-status-warning",
      };
    }
  };

  const handleMarkComplete = () => {
    setIsCompleted(!isCompleted);
    // In a real app, you would save this to localStorage or a backend
  };

  const renderQuiz = () => {
    if (quizCompleted) {
      return (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Quiz Completed!
          </h3>

          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary-100">
              <span className="text-3xl font-bold text-primary-500">
                {score}/{quizQuestions.length}
              </span>
            </div>
          </div>

          <p className="text-gray-600 mb-8">
            {score === quizQuestions.length
              ? "Perfect score! You're a climate knowledge champion!"
              : score >= 3
              ? "Great job! You have a good understanding of climate issues."
              : "Keep learning! Climate literacy is a journey."}
          </p>

          <button
            onClick={handleResetQuiz}
            className="flex items-center justify-center mx-auto bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-700"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Take Quiz Again
          </button>
        </div>
      );
    }

    const question = quizQuestions[currentQuestion];

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-primary-700 py-4 px-6">
          <div className="flex justify-between items-center">
            <span className="text-white font-medium">
              Question {currentQuestion + 1} of {quizQuestions.length}
            </span>
            <span className="text-white">Score: {score}</span>
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-xl font-medium text-gray-800 mb-6">
            {question.question}
          </h3>

          <div className="space-y-3 mb-8">
            {question.options.map((option, index) => (
              <button
                key={index}
                className={`w-full text-left p-4 rounded-lg border ${
                  selectedAnswer === index
                    ? index === question.correctAnswer
                      ? "bg-green-100 border-green-500"
                      : "bg-red-100 border-red-500"
                    : "border-gray-200 hover:border-primary-500"
                }`}
                onClick={() => handleAnswerSelect(index)}
                disabled={showResult}
              >
                <div className="flex items-center">
                  <div className="flex-1">{option}</div>
                  {showResult && index === question.correctAnswer && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                  {showResult &&
                    selectedAnswer === index &&
                    index !== question.correctAnswer && (
                      <X className="w-5 h-5 text-red-500" />
                    )}
                </div>
              </button>
            ))}
          </div>

          {showResult && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700">{question.explanation}</p>
            </div>
          )}

          {showResult && (
            <button
              onClick={handleNextQuestion}
              className="w-full py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-700"
            >
              {currentQuestion < quizQuestions.length - 1
                ? "Next Question"
                : "Complete Quiz"}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderSimulator = () => {
    const allChoicesMade =
      simulatorChoices.commute &&
      simulatorChoices.lunch &&
      simulatorChoices.evening;

    return (
      <div>
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Plan Your Day Simulator
          </h3>
          <p className="text-gray-600 mb-6">
            Make choices for your day and see how they impact your carbon
            footprint. Each choice contributes to your daily emissions in
            kilograms of CO₂ equivalent.
          </p>

          <div className="space-y-8">
            {/* Commute Options */}
            <div>
              <h4 className="text-lg font-medium text-primary-500 mb-3">
                Morning Commute
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {simulatorOptions.commute.map((option) => (
                  <button
                    key={option.id}
                    className={`p-3 rounded-lg border text-center ${
                      simulatorChoices.commute === option
                        ? "bg-primary-100 border-primary-500"
                        : "border-gray-200 hover:border-primary-500"
                    }`}
                    onClick={() => handleSimulatorChoice("commute", option)}
                  >
                    <option.icon className="w-6 h-6 mx-auto mb-2 text-gray-700" />
                    <div className="text-sm font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">
                      {option.emissions} kg CO₂e
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Lunch Options */}
            <div>
              <h4 className="text-lg font-medium text-primary-500 mb-3">
                Lunch
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {simulatorOptions.lunch.map((option) => (
                  <button
                    key={option.id}
                    className={`p-3 rounded-lg border text-center ${
                      simulatorChoices.lunch === option
                        ? "bg-primary-100 border-primary-500"
                        : "border-gray-200 hover:border-primary-500"
                    }`}
                    onClick={() => handleSimulatorChoice("lunch", option)}
                  >
                    <option.icon className="w-6 h-6 mx-auto mb-2 text-gray-700" />
                    <div className="text-sm font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">
                      {option.emissions} kg CO₂e
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Evening Options */}
            <div>
              <h4 className="text-lg font-medium text-primary-500 mb-3">
                Evening Activity
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {simulatorOptions.evening.map((option) => (
                  <button
                    key={option.id}
                    className={`p-3 rounded-lg border text-center ${
                      simulatorChoices.evening === option
                        ? "bg-primary-100 border-primary-500"
                        : "border-gray-200 hover:border-primary-500"
                    }`}
                    onClick={() => handleSimulatorChoice("evening", option)}
                  >
                    <option.icon className="w-6 h-6 mx-auto mb-2 text-gray-700" />
                    <div className="text-sm font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">
                      {option.emissions} kg CO₂e
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {allChoicesMade && (
          <motion.div
            className="bg-primary-100 rounded-lg p-6 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              Your Daily Carbon Footprint
            </h3>

            <div className="text-5xl font-bold text-primary-500 mb-4">
              {calculateTotalEmissions()}{" "}
              <span className="text-2xl">kg CO₂e</span>
            </div>

            <p className={`text-lg ${getSimulatorFeedback().color} mb-6`}>
              {getSimulatorFeedback().text}
            </p>

            <button
              onClick={handleResetSimulator}
              className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
            >
              Try Different Choices
            </button>
          </motion.div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Quiz & Mini-Games<span className="text-edu-orange">!</span>
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      <div className="bg-primary-100 p-6 rounded-lg mb-8">
        <p className="text-lg text-gray-800">
          Test your knowledge and practice making climate-friendly decisions
          through interactive activities. These exercises help reinforce what
          you've learned and visualize the impact of your choices.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-8">
        <button
          className={`px-6 py-3 ${
            activeTab === "quiz"
              ? "border-b-2 border-primary-500 text-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveTab("quiz")}
        >
          Climate Knowledge Quiz
        </button>
        <button
          className={`px-6 py-3 ${
            activeTab === "simulator"
              ? "border-b-2 border-primary-500 text-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveTab("simulator")}
        >
          Day Planner Simulator
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="mb-16">
        {activeTab === "quiz" ? renderQuiz() : renderSimulator()}
      </div>

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/carbon-footprint"
        prevText="Previous: Carbon Footprint"
        nextLink="/action-toolkit"
        nextText="Next: Action Toolkit"
        onMarkComplete={handleMarkComplete}
        isCompleted={isCompleted}
      />
    </div>
  );
};

export default Quiz;
